import { Icon } from '@/components/icon/Icon';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { Translate } from 'next-translate';
import {useCurrencyFormatter} from "@/lib/_hooks/useUserCurrency.ts";

interface DoubleLineCellProps {
	line_1: string | number | ReactNode;
	line_2: string | number | ReactNode;
	variant?: 'default' | 'nostyle';
}

interface TournamentDateCellProps {
	dateStart: Date;
	dateEnd: Date;
	tournamentType: string;
}

interface DateAndHourDataCellProps {
	date: number | null;
}

interface QuantityDataCellProps {
	quantity: number | null;
	freezed?: number;
}
interface TotalPrizeDataCellProps {
	t: Translate;
	prize: number;
	firstPrize?: number;
	subscriptions: number;
	handleInfo?: () => void;
}

export const DoubleLineCell = ({ ...props }: DoubleLineCellProps) => {
	const { line_1, line_2, variant = 'default' } = props;

	return (
		<>
			<div>{line_1}</div>
			<div className={`${variant == 'default' && 'mt-xs text-[12px] text-dark-300'}`}>{line_2}</div>
		</>
	);
};

export const TournamentDateCell = ({ ...props }: TournamentDateCellProps) => {
	const { dateStart, dateEnd, tournamentType } = props;
	const startAtFormatted = format(new Date(dateStart), 'MMM dd - HH:mm');
	const endAtFormatted = ['1H', '4H'].includes(tournamentType)
		? format(new Date(dateEnd), 'HH:mm')
		: format(new Date(dateEnd), 'MMM dd - HH:mm');

	return (
		<span className='flex items-center'>
			{startAtFormatted}
			<Icon name='arrowRightIcon' className='mx-lg !text-dark-300' />
			{endAtFormatted}
		</span>
	);
};

export const DateAndHourDataCell = ({ date }: DateAndHourDataCellProps) => {
	return (
		<>
			<p>{date ? format(new Date(date), 'MM/dd/yyyy') : '-'}</p>
			<p className='mt-xs text-[12px] text-dark-300'>{date ? format(new Date(date), 'HH:mm') : '-'}</p>
		</>
	);
};

export const QuantityDataCell = ({ ...props }: QuantityDataCellProps) => {
	const { quantity, freezed } = props;

	return (
		<div className='flex items-center'>
			<Icon className='mr-2' name='tralentQuantityIcon' />
			{freezed != null ? freezed + '/' : ''}
			{quantity ? quantity : '-'}
		</div>
	);
};

export const TotalPrizeDataCell = ({ ...props }: TotalPrizeDataCellProps) => {
	const { t, prize, firstPrize, subscriptions, handleInfo } = props;
	const {amountWithUserCurrency} = useCurrencyFormatter();

	return prize && subscriptions > 1 ? (
		<div className='flex items-center justify-between gap-2xl'>
			<div>
				{firstPrize && (
					<div className='mb-xs text-warning-500'>
						<span className='font-semibold'>{t('common:1st')}:</span> {amountWithUserCurrency(firstPrize)}
					</div>
				)}
				<div className='text-sm text-dark-300'>
					{t('common:tot')}. {amountWithUserCurrency(prize)}
				</div>
			</div>
			{handleInfo && subscriptions > 1 && (
				<div
					className='btn-transition'
					onClick={(e) => {
						e.stopPropagation();
						handleInfo();
					}}>
					<Icon name='infoCircleDashedIcon' variant='primary' />
				</div>
			)}
		</div>
	) : (
		<p>{t('common:no_prize')}</p>
	);
};
